import { useMemo } from 'react';
import { Badge, Card, cx } from '../../../components/admin/console';
import { useOtaDevices, usePointers } from './data/hooks';
import { channelBadgeTone, channelLabel } from './data/channelLabels';
import type { OtaAppId, OtaChannelId, OtaDevice } from './data/types';

interface CheckLogRow {
  t: string;
  device: string;
  vessel: string;
  app: OtaAppId | 'launcher';
  channel: OtaChannelId;
  expected: string | number;
  running: string | number;
  stale: boolean;
  status: 'up-to-date' | 'stale' | 'offline';
  rtt: number;
}

const APP_KEYS: Array<OtaAppId> = ['vn', 'th', 'vt', 'va', 'vc', 'launcher'];

// Synthesise a recent ~200-row poll stream from the mock device list.
// Replaced in PR #6 by the real /api/admin/neptune/updates feed.
function buildRows(
  devices: OtaDevice[],
  pointerOf: (channel: string, app: OtaAppId) => { current: number } | undefined,
): CheckLogRow[] {
  const out: CheckLogRow[] = [];
  const now = new Date('2026-04-24T03:00:00Z').getTime();
  let s = 42;
  const rand = () => {
    s = (s * 9301 + 49297) % 233280;
    return s / 233280;
  };
  const sample = devices.slice(0, 40);
  for (let i = 0; i < 200; i++) {
    const d = sample[i % sample.length];
    const app = APP_KEYS[Math.floor(rand() * APP_KEYS.length)];
    const ch = d.channel;
    const expected = pointerOf(ch, app)?.current ?? '—';
    const running = d.vnVc && app === 'vn' ? d.vnVc : expected;
    const stale = running !== expected && rand() < 0.2;
    out.push({
      t: new Date(now - i * 43000).toISOString().slice(11, 19),
      device: d.id,
      vessel: d.name,
      app,
      channel: ch,
      expected,
      running,
      stale,
      status: d.connectivity === 'none' ? 'offline' : stale ? 'stale' : 'up-to-date',
      rtt: 40 + Math.floor(rand() * 120),
    });
  }
  return out;
}

export function OtaCheckLogPage() {
  const { data: devices = [] } = useOtaDevices();
  const { data: pointers = [] } = usePointers();

  const rows = useMemo(() => {
    const pointerOf = (channel: string, app: OtaAppId) =>
      pointers.find((p) => p.channel === channel && p.app === app);
    return buildRows(devices, pointerOf);
  }, [devices, pointers]);

  return (
    <div className="px-8 py-6 space-y-6">
      <div>
        <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-cyan-400">
          Observability
        </div>
        <h1 className="text-2xl font-bold text-white mt-1">Device check log</h1>
        <p className="text-sm text-slate-400 mt-1">
          Live manifest-poll stream. Devices check in every ~30s when connected.
        </p>
      </div>
      <Card padded={false} className="overflow-hidden">
        <div className="max-h-[620px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-800/60 sticky top-0 z-10">
              <tr className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                <th className="text-left px-4 py-2.5">Time (UTC)</th>
                <th className="text-left px-2 py-2.5">Device</th>
                <th className="text-left px-2 py-2.5">App</th>
                <th className="text-left px-2 py-2.5">Channel</th>
                <th className="text-right px-2 py-2.5">Expected</th>
                <th className="text-right px-2 py-2.5">Running</th>
                <th className="text-left px-2 py-2.5">Status</th>
                <th className="text-right px-4 py-2.5">RTT</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr
                  key={i}
                  className={cx(
                    'border-t border-slate-700/40 density-row',
                    r.status === 'stale' && 'bg-amber-500/5',
                  )}
                >
                  <td className="px-4 py-2 font-mono text-[11px] text-slate-400">{r.t}</td>
                  <td className="px-2 py-2">
                    <div className="text-xs font-semibold text-white">{r.vessel}</div>
                    <div className="text-[10px] text-slate-500 font-mono">{r.device}</div>
                  </td>
                  <td className="px-2 py-2 font-mono text-[11px] text-slate-300">{r.app}</td>
                  <td className="px-2 py-2">
                    <Badge tone={channelBadgeTone(r.channel)}>{channelLabel(r.channel)}</Badge>
                  </td>
                  <td className="px-2 py-2 text-right font-mono text-slate-300">
                    {r.expected}
                  </td>
                  <td className="px-2 py-2 text-right font-mono">
                    <span className={r.stale ? 'text-amber-400' : 'text-white'}>{r.running}</span>
                  </td>
                  <td className="px-2 py-2">
                    <Badge
                      tone={
                        r.status === 'up-to-date'
                          ? 'success'
                          : r.status === 'stale'
                            ? 'warning'
                            : 'neutral'
                      }
                      dot
                    >
                      {r.status}
                    </Badge>
                  </td>
                  <td className="px-4 py-2 text-right font-mono text-[11px] text-slate-400">
                    {r.rtt}ms
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

export default OtaCheckLogPage;
